import { useContext, useMemo, useRef, useState, useCallback } from 'react'
import AuthContext from '../../context/AuthContext'
import { QueryKey, useQuery } from '@tanstack/react-query'
import { getAuth } from 'firebase/auth'
import ApiContext from '../../context/ApiContext'
import { StripeSubscription } from '../../models/StripeSubscription'

export const getCompanySubscriptionQueryKey = (companyId?: string): QueryKey => ['subscription', companyId]

interface UseCompanySubscriptionResult {
	companySubscription: StripeSubscription | null | undefined
	isPersonalSubscription: boolean
	isTrialUsed: boolean
	isLoading: boolean
	error: Error | null
	startPolling: (interval: number) => void
	stopPolling: () => void
	shouldShowLoading: boolean
}

type CompanySubscriptionResponse = {
	subscription: StripeSubscription | null
	isPersonalSubscription: boolean
	isTrialUsed: boolean
}

const useCompanySubscription = (): UseCompanySubscriptionResult => {
	const auth = getAuth()
	const authContext = useContext(AuthContext)
	const apiContext = useContext(ApiContext)
	const companyId = authContext.company?.id
	const refetchIntervalRef = useRef<number | false>(false)
	const [isPolling, setIsPolling] = useState(false)
	const {
		data: companySubscription,
		isLoading,
		isFetching,
		error,
	} = useQuery({
		queryKey: getCompanySubscriptionQueryKey(companyId),
		queryFn: async ({ signal }): Promise<CompanySubscriptionResponse | null> => {
			const idToken = await auth.currentUser?.getIdToken(true)
			const response = await fetch(`${apiContext.apiUrl}/companies/${companyId}/subscription`, {
				headers: {
					Authorization: `Bearer ${idToken}`,
				},
				signal,
			})

			if (response.status === 404) {
				return null
			}

			if (!response.ok) {
				throw new Error(await response.text())
			}
			const data = await response.json()
			return {
				subscription: data.subscription
					? {
							...data.subscription,
							cancelAt: data.subscription.cancelAt ? new Date(data.subscription.cancelAt) : undefined,
						}
					: null,
				isPersonalSubscription: data.isPersonalSubscription,
				isTrialUsed: data.isTrialUsed,
			}
		},
		enabled: !!companyId,
		refetchInterval: refetchIntervalRef.current,
	})

	const startPolling = useCallback((interval: number) => {
		setIsPolling(true)
		refetchIntervalRef.current = interval
	}, [])

	const stopPolling = useCallback(() => {
		setIsPolling(false)
		refetchIntervalRef.current = false
	}, [])

	const shouldShowLoading = useMemo(() => {
		return isLoading || isFetching || isPolling
	}, [isLoading, isFetching, isPolling])

	return {
		companySubscription: companySubscription?.subscription ?? null,
		isPersonalSubscription: companySubscription?.isPersonalSubscription ?? false,
		isTrialUsed: companySubscription?.isTrialUsed ?? true,
		isLoading,
		error,
		startPolling,
		stopPolling,
		shouldShowLoading,
	}
}

export default useCompanySubscription
